import { createSelector } from "@reduxjs/toolkit";
import { selectViewerPixPolygons } from "../DesignTab/pixpolygonSelectors";
import { selectStreetViewItemsById } from "./viewerSelectors";
import { selectCeilingDevice3dItems, selectNonPublishedDevice3dItems, CEILING_DEVICE_3D_ID_PREFIX, PREVIEW_DEVICE_3D_ID_PREFIX } from "./ceilingDevice3dSelectors";
import { selectCeilingGrid3dItems } from "./ceilingGrid3dSelectors";
import { selectValidationTile3dItems } from "./validationTile3dSelectors";
import { selectMlHeatmap3dItems } from "./mlHeatmap3dSelectors";
import type { IRootState } from "../app/store";

/** 2D owns object visibility. 3D adds walls and slabs as permanent spatial context. */
export const selectSceneObjectIds = createSelector(
  [selectViewerPixPolygons],
  polygons => new Set(polygons.flatMap(polygon => [polygon.id, ...(polygon.entityId ? [String(polygon.entityId)] : [])])),
);

export const selectSceneStreetViewItems = createSelector(
  [selectStreetViewItemsById, selectSceneObjectIds],
  (items, ids) => Array.from(items.values()).filter(item =>
    item.instanceClass === "IfcWall" || item.instanceClass === "IfcWallStandardCase"
    || item.instanceClass === "IfcSlab" || ids.has(String(item._id))),
);

export const selectSceneStreetViewSpatialItems = createSelector(
  [selectSceneStreetViewItems],
  items => items.map(item => ({ id: String(item._id), centerPoint: item.centerPoint, bbox: item.bbox, polygon: item.polygon })),
);

export const selectSceneStreetViewItemIds = createSelector(
  [selectSceneStreetViewItems],
  items => new Set(items.map(item => String(item._id))),
);

export const selectSceneLocal3dItems = createSelector(
  [selectSceneObjectIds, (state: IRootState) => state.design.activeModule,
    selectCeilingDevice3dItems, selectNonPublishedDevice3dItems,
    selectCeilingGrid3dItems, selectValidationTile3dItems, selectMlHeatmap3dItems],
  (ids, module, devices, previews, grids, validations, heatmap) => {
    const visibleDevices = module === "CEILING" ? devices.filter(({ item }) =>
      ids.has(String(item._id).slice(CEILING_DEVICE_3D_ID_PREFIX.length))) : [];
    const visiblePreviews = module === "CEILING" ? previews.filter(({ item }) =>
      ids.has(String(item._id).slice(PREVIEW_DEVICE_3D_ID_PREFIX.length))) : [];
    return [...validations, ...visiblePreviews, ...visibleDevices,
      ...grids, ...heatmap];
  },
);
